// Subscription tier utility functions
// Tier hierarchy: free < starter < premium < family < folio
// Have now changed the tiering

const TIERS = {
  free: 0,
  starter: 1,
  folio: 2,
  premium: 3,
  family: 4,
  guest: -1
};

const TIER_FEATURES = {
  // Child profiles limits
  childProfiles: {
    free: 0,
    starter: 0,
    premium: Infinity,
    family: Infinity,
    folio: 0,
    guest: 0
  },
  // Avatar limits
  avatars: {
    free: 0,
    starter: 0,
    premium: Infinity,
    family: Infinity,
    folio: 0,
    guest: 0
  }
};

/**
 * Check if a tier can access a specific feature
 * @param {string} userTier - User's current subscription tier
 * @param {string} requiredTier - Minimum tier required for feature
 * @returns {boolean}
 */
export const canAccessFeature = (userTier, requiredTier) => {
  const userLevel = TIERS[userTier] || 0;
  const requiredLevel = TIERS[requiredTier] || 0;
  return userLevel >= requiredLevel;
};

/**
 * Check if user can create more child profiles
 * @param {string} tier - User's subscription tier
 * @param {number} currentCount - Current number of children
 * @returns {boolean}
 */
export const canAddChild = (tier, currentCount) => {
  const limit = TIER_FEATURES.childProfiles[tier] || 0;
  return currentCount < limit;
};

/**
 * Check if user can create more avatars
 * @param {string} tier - User's subscription tier
 * @param {number} currentCount - Current number of avatars
 * @returns {boolean}
 */
export const canAddAvatar = (tier, currentCount) => {
  const limit = TIER_FEATURES.avatars[tier] || 0;
  return currentCount < limit;
};

/**
 * Get the limit message for a feature based on tier
 * @param {string} feature - Feature name
 * @param {string} tier - User's subscription tier
 * @returns {string}
 */
export const getFeatureLimitMessage = (feature, tier) => {
  const messages = {
    textToSpeech: 'Text-to-Speech is available from Starter tier and above',
    offlineReading: 'Offline Reading is available from Starter tier and above',
    avatarBuilding: 'Avatar Building is available from Starter tier and above',
    statistics: 'Statistics are available for Premium tier and above',
    phonics: 'Phonics option is available for Premium tier and above',
    storyEditing: 'Story Editing is available for Family tier and above',
    customDetails: 'Custom Details field is available for Family tier and above'
  };
  return messages[feature] || `This feature requires ${tier} tier or higher`;
};

/**
 * Get required tier for a feature
 * @param {string} feature - Feature name
 * @returns {string}
 */
export const getRequiredTier = (feature) => {
  const requirements = {
    textToSpeech: 'starter',
    offlineReading: 'starter',
    avatarBuilding: 'starter',
    statistics: 'premium',
    phonics: 'premium',
    storyEditing: 'family',
    customDetails: 'family'
  };
  return requirements[feature] || 'premium';
};

/**
 * Check if user can access text-to-speech
 * @param {string} tier - User's subscription tier
 * @returns {boolean}
 */
export const canUseTextToSpeech = (tier) => {
  return canAccessFeature(tier, 'starter');
};

/**
 * Check if user can download for offline reading
 * @param {string} tier - User's subscription tier
 * @returns {boolean}
 */
export const canUseOfflineReading = (tier) => {
  return canAccessFeature(tier, 'starter');
};

/**
 * Check if user can access avatar building
 * @param {string} tier - User's subscription tier
 * @returns {boolean}
 */
export const canAccessAvatarBuilding = (tier) => {
  return canAccessFeature(tier, 'starter');
};

/**
 * Check if user can access statistics
 * @param {string} tier - User's subscription tier
 * @returns {boolean}
 */
export const canAccessStatistics = (tier) => {
  return canAccessFeature(tier, 'premium');
};

/**
 * Check if user can use phonics option
 * @param {string} tier - User's subscription tier
 * @returns {boolean}
 */
export const canUsePhonics = (tier) => {
  return canAccessFeature(tier, 'premium');
};

/**
 * Check if user can edit stories
 * @param {string} tier - User's subscription tier
 * @returns {boolean}
 */
export const canEditStory = (tier) => {
  return canAccessFeature(tier, 'family');
};

/**
 * Check if user can use custom details field
 * @param {string} tier - User's subscription tier
 * @returns {boolean}
 */
export const canUseCustomDetails = (tier) => {
  return canAccessFeature(tier, 'family');
};

/**
 * Get child profile limit for tier
 * @param {string} tier - User's subscription tier
 * @returns {number}
 */
export const getChildProfileLimit = (tier) => {
  return TIER_FEATURES.childProfiles[tier] || 0;
};

/**
 * Get avatar limit for tier
 * @param {string} tier - User's subscription tier
 * @returns {number}
 */
export const getAvatarLimit = (tier) => {
  return TIER_FEATURES.avatars[tier] || 0;
};
